/**
 * @jsx jsx
 */
import React from 'react';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@compiled/react';
import { type StrictXCSSProp } from '@atlaskit/css';
import { analyticsAttributes, withAnalyticsContextData } from '../../common/utils/analytics';
import { FadeIn } from './fade-in';
import Heading from '@atlaskit/heading';

type SectionProps = {
	children?: React.ReactNode;
	sectionId: string;
	tag?: keyof JSX.IntrinsicElements;
	title?: React.ReactNode;
	xcss?: StrictXCSSProp<'width' | 'borderTop', never>;
};

type SectionAnalyticsContext = {
	attributes: {
		group: string;
		groupItemsCount: number;
	};
};

const styles = cssMap({
	sectionBase: {
		paddingTop: token('space.100'),
		paddingRight: 0,
		paddingBottom: token('space.075'),
		paddingLeft: 0,
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '-1px',
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	list: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
	},
	box: {
		marginLeft: token('space.150'),
		marginRight: token('space.150'),
		marginTop: '0',
		marginBottom: token('space.050'),
	},
	titleBase: {
		marginTop: token('space.075'),
		marginLeft: token('space.100'),
		marginBottom: token('space.075'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
	},
});

const SectionContainerComponent = ({
	sectionId,
	title,
	children,
	xcss,
	tag: Tag = 'ul',
}: SectionProps) => {
	const hasChildren = React.Children.toArray(children).some(Boolean);
	if (!hasChildren) {
		return null;
	}

	return (
		<section
			css={styles.sectionBase}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required with `xcss`
			className={xcss}
			data-testid={`${sectionId}__section`}
		>
			<div css={styles.box}>
				<FadeIn>
					{title && (
						<Heading as="h2" size="xxsmall">
							<Box xcss={styles.titleBase}>{title}</Box>
						</Heading>
					)}
				</FadeIn>
				<Tag css={styles.list}>{children}</Tag>
			</div>
		</section>
	);
};

export const SectionContainer: React.ComponentType<SectionProps> = withAnalyticsContextData<SectionProps, SectionAnalyticsContext>(
	(props) => ({
		...analyticsAttributes({
			group: props.sectionId,
			groupItemsCount: React.Children.toArray(props.children).filter(React.isValidElement).length,
		}),
	}),
)(SectionContainerComponent);
